import _unionBy from 'lodash.unionby';
import { computed, makeAutoObservable } from 'mobx';

import {
  LARGE_LIST_ROW_MODEL_DEFAULT_RECORDS_LIMIT,
  TEntityGridConstraints,
  TEntityGridConstraintsState,
  TEntityGridFilter,
  TEntityGridPages,
  TEntityGridPagination,
  TEntityGridRowModelNameTypes,
  TEntityGridRowModels,
  TEntityGridSort,
} from '../../core';

const pagingInitialState = (): TEntityGridPagination => ({
  page: 1,
  size: 40,
  totalPages: 1,
  totalElements: 40,
});

type EntityGridConstraintsStoreParams = TEntityGridRowModels & {
  initialState?: TEntityGridConstraints;
};

export class EntityGridConstraints {
  protected sorting: TEntityGridSort[] = [];
  private filters: TEntityGridFilter | null = null;
  private pageInfo: TEntityGridPagination = pagingInitialState();

  public rowModelType: TEntityGridRowModelNameTypes;
  public recordsLimit: number = LARGE_LIST_ROW_MODEL_DEFAULT_RECORDS_LIMIT;

  constructor(params?: EntityGridConstraintsStoreParams) {
    this.updateState(params?.initialState);

    this.rowModelType = params?.rowModelType ?? 'largeList';

    if (params?.rowModelType === 'largeList') {
      this.recordsLimit = params?.recordsLimit;
    }

    // Maybe a separate implementation is needed for different TEntityGridRowModels?
    if (
      params?.rowModelType === 'largeList' &&
      typeof params?.recordsLimit === 'number'
    ) {
      // used when loading large number records without pagination
      this.updatePageInfo({
        page: 1,
        size: params.recordsLimit ?? LARGE_LIST_ROW_MODEL_DEFAULT_RECORDS_LIMIT,
      });
    }

    makeAutoObservable(this, { data: computed });
  }

  public get data() {
    return {
      sorting: this.sorting,
      filter: this.filters,
      pageInfo: this.pageInfo,
    };
  }

  private updateState(state?: TEntityGridConstraintsState) {
    state?.pageInfo && this.updatePageInfo(state?.pageInfo);

    if (state?.filter) {
      this.filters = state?.filter;
    }

    if (state?.sorting) {
      this.sorting = state.sorting.filter(<T>(sort?: T): sort is T => !!sort);
    }
  }

  public updateConstraints(constraints?: TEntityGridConstraintsState) {
    this.updateState(constraints);
  }

  public updatePageInfo(page: TEntityGridPages) {
    this.pageInfo.page = page.page;
    this.pageInfo.size = page.size;
  }

  public setFilters(filters: TEntityGridFilter[]) {
    // also reseting paging for new filetered result and display first page
    this.pageInfo = pagingInitialState();
    this.filters = filters;
  }

  public updateFilters(filters: TEntityGridFilter) {
    this.filters = { ...this.filters, ...filters };
  }

  public setSort(sort: TEntityGridSort[]) {
    this.sorting = sort;
  }

  public updateSort(sort: TEntityGridSort[]) {
    this.sorting = _unionBy(sort, this.sorting, 'name');
  }

  public reset() {
    this.sorting = [];
    this.filters = null;
    this.pageInfo = pagingInitialState();
  }
}
